import { atom } from 'jotai';
import request from 'lib/request';
import { mutationAtomWithLoadable } from 'lib/atoms';
import { BundleOfSticksOwnershipResponse } from 'types/api-responses';
import {
  bundleOfSticksDetailsLoadableAtom,
  crmTractFormAtom,
  tractEnumAtom,
} from './atoms';
import { InterestConversionData } from './types';
import { allowedValuesFor, fixedNumber, isUnusedField } from './utils';
import { buildInterestConversionData } from './bundle-of-sticks-utils';

/**
 * ============================================================================
 * INTERESTS CALCULATOR CONFIGURATION
 * ============================================================================
 * Configuration fetched from API that contains interests by instrument type,
 * default royalty rates, and other calculation defaults.
 */

/**
 * API response type for /interests_calculator/configuration
 */
export interface InterestsCalculatorConfiguration {
  interestsByInstrumentType: Record<string, string[]>;
  defaultRoyaltyRates: Record<string, number>;
  defaultRoyaltyRate: number;
  defaultRoyaltyBurdens: number;
  defaultOrriBurdens: number;
}

/**
 * Async atom to fetch the interests calculator configuration.
 */
export const interestsCalculatorConfigAtom = atom(async () => {
  const response = await request.get<InterestsCalculatorConfiguration>(
    '/interests_calculator/configuration'
  );
  return response as InterestsCalculatorConfiguration;
});

/**
 * Helper to convert string values to numbers (handles BigDecimal serialization from Rails).
 * @param {unknown} value - The value to parse
 * @return {number | null} The parsed number or null
 */
const parseNumericValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }
  return null;
};

/**
 * Normalize BOS response to ensure all numeric fields are proper numbers.
 * Rails BigDecimal can serialize as strings in JSON.
 * @param {BundleOfSticksOwnershipResponse} response - The response to normalize
 * @return {BundleOfSticksOwnershipResponse} The normalized response with proper number types
 */
const normalizeBosResponse = (
  response: BundleOfSticksOwnershipResponse
): BundleOfSticksOwnershipResponse => ({
  id: response.id,
  tract: response.tract,
  subtract: response.subtract,
  executive: parseNumericValue(response.executive),
  bonus: parseNumericValue(response.bonus),
  delay: parseNumericValue(response.delay),
  royalty: parseNumericValue(response.royalty),
  wi: parseNumericValue(response.wi),
  orri: parseNumericValue(response.orri),
  nri: parseNumericValue(response.nri),
  nma: parseNumericValue(response.nma),
  postBurdenNma: parseNumericValue(response.postBurdenNma),
  nra: parseNumericValue(response.nra),
  netAcreageUnits: response.netAcreageUnits,
  grossAcres: parseNumericValue(response.grossAcres),
  leaseRoyaltyRate: parseNumericValue(response.leaseRoyaltyRate),
  leaseRoyaltyType: response.leaseRoyaltyType,
  royaltyBurdens: parseNumericValue(response.royaltyBurdens),
  orriBurdens: parseNumericValue(response.orriBurdens),
  estate: response.estate,
  instrumentType: response.instrumentType,
  createdAt: response.createdAt,
  updatedAt: response.updatedAt,
});

/**
 * The interests the calculator configuration lists for an interest type,
 * matched exactly: what the preview calculates, and whether it runs at all.
 * @param {InterestsCalculatorConfiguration | null | undefined} config - The configuration from API
 * @param {string | null | undefined} type - The interest type's value
 * @return {string[] | undefined} The interests; undefined for a type it does
 *   not list, or lists with none
 */
export const interestsFor = (
  config: InterestsCalculatorConfiguration | null | undefined,
  type: string | null | undefined
): string[] | undefined => {
  const interests = type
    ? config?.interestsByInstrumentType?.[type]
    : undefined;
  return interests?.length ? interests : undefined;
};

/**
 * Get the default royalty rate for a given instrument type, matched exactly.
 * Returns null if config is not available.
 * @param {string} instrumentType - The instrument type's value
 * @param {InterestsCalculatorConfiguration | null} config - The configuration from API
 * @return {number | null} The default royalty rate as a decimal, or null if config unavailable
 */
export const getDefaultRoyaltyRateForInstrumentType = (
  instrumentType: string,
  config?: InterestsCalculatorConfiguration | null
): number | null => {
  if (!config || !config.defaultRoyaltyRates) return null;

  return (
    config.defaultRoyaltyRates[instrumentType] ??
    config.defaultRoyaltyRate ??
    null
  );
};

/**
 * Convert a percentage value (e.g., 25.5) to decimal (e.g., 0.255).
 * Returns null if the value is null or undefined.
 * @param {number | null} value - The percentage value to convert
 * @return {number | null} The decimal value or null
 */
const percentageToDecimal = (value: number | null): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  return value / 100;
};

/**
 * Validate if all required fields are present for the calculation.
 */
interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

const validateCalculationInputs = (params: {
  instrumentType: string;
  grossAcres: number | null;
  tractNetAcres: number | null;
  netAcreageUnits: string;
}): ValidationResult => {
  const errors: string[] = [];

  if (!params.instrumentType) {
    errors.push('Instrument Type is required');
  }

  if (params.grossAcres === null || params.grossAcres <= 0) {
    errors.push('Gross Acres must be a positive number');
  }

  if (params.tractNetAcres === null || params.tractNetAcres <= 0) {
    errors.push('Tract Net Acres must be a positive number');
  }

  if (
    !params.netAcreageUnits ||
    !['NMA', 'NRA'].includes(params.netAcreageUnits)
  ) {
    errors.push('Net Acreage Units must be NMA or NRA');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * API request payload for interests calculator (snake_case to match backend).
 */
interface InterestsCalculatorRequest {
  input_source: 'acreage';
  interests_to_calculate: string[];
  gross_acres: number;
  tract_net_acres: number;
  net_acreage_units: string;
  lease_royalty_rate: number;
  royalty_burdens?: number;
  orri_burdens?: number;
}

/**
 * API response from interests calculator (jbuilder transforms to camelCase).
 */
interface InterestsCalculatorResponse {
  inputSource: string;
  formulaInputs: {
    netAcres?: number;
    netAcreageUnits?: string;
    grossAcres: number;
    leaseRoyaltyRate: number;
    royaltyBurdens?: number;
    orriBurdens?: number;
  };
  interests: {
    executive?: number;
    bonus?: number;
    delay?: number;
    royalty?: number;
    wi?: number;
    orri?: number;
    nri?: number;
    nma?: number;
    postBurdenNMA?: number;
    nra?: number;
  };
}

/**
 * Convert calculator response to BundleOfSticksOwnershipResponse format.
 * @param {InterestsCalculatorResponse} response - The calculator response to convert
 * @return {BundleOfSticksOwnershipResponse} The converted ownership response
 */
const convertCalculatorResponse = (
  response: InterestsCalculatorResponse
): BundleOfSticksOwnershipResponse => {
  const { formulaInputs, interests } = response;
  return {
    id: null as unknown as number,
    executive: interests.executive ?? null,
    bonus: interests.bonus ?? null,
    delay: interests.delay ?? null,
    royalty: interests.royalty ?? null,
    wi: interests.wi ?? null,
    orri: interests.orri ?? null,
    nri: interests.nri ?? null,
    nma: interests.nma ?? null,
    postBurdenNma: interests.postBurdenNMA ?? null,
    nra: interests.nra ?? null,
    netAcreageUnits: formulaInputs.netAcreageUnits?.toLowerCase() as
      'nma' | 'nra' | null,
    grossAcres: formulaInputs.grossAcres,
    leaseRoyaltyRate: formulaInputs.leaseRoyaltyRate,
    leaseRoyaltyType: null,
    royaltyBurdens: formulaInputs.royaltyBurdens ?? null,
    orriBurdens: formulaInputs.orriBurdens ?? null,
    estate: null,
    instrumentType: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

/**
 * Main atom to calculate interests.
 * Uses mutationAtomWithLoadable for automatic loading state management.
 * Returns null if validation fails (form not ready for calculation).
 */
export const [calculateInterestsAtom, calculateInterestsLoadableAtom] =
  mutationAtomWithLoadable(
    async (get): Promise<BundleOfSticksOwnershipResponse | null> => {
      const form = await get(crmTractFormAtom);
      const config = await get(interestsCalculatorConfigAtom);

      // Get form values
      const instrumentType = get(form.instrumentType).value;
      const grossAcres = get(form.grossAcres).value;
      const tractNetAcres = get(form.tractNetAcres).value;
      const netAcreageUnits = get(form.netAcreageUnits).value;
      const leaseRoyaltyRatePercent = get(form.leaseRoyaltyRate).value;
      const royaltyBurdensPercent = get(form.royaltyBurdens).value;
      const orriBurdensPercent = get(form.orriBurdens).value;

      // Validate required inputs
      const validation = validateCalculationInputs({
        instrumentType,
        grossAcres,
        tractNetAcres,
        netAcreageUnits,
      });

      if (!validation.isValid) {
        return null;
      }

      // The interests the calculator lists for the type (interestsFor, which
      // the form's preview switch reads too), so a calculation still pending
      // when the type changes sends nothing for a type it does not list.
      const interestsToCalculate = interestsFor(config, instrumentType);
      if (!interestsToCalculate) {
        return null;
      }

      // The calculator takes no interest type, so the server's rules for the
      // type's fields (GET /crm/enums) decide what it is sent. A type the
      // server does not list gets no preview, as the form takes no input for it.
      const allowed = allowedValuesFor(
        (await get(tractEnumAtom)).instrumentType,
        instrumentType
      );
      if (!allowed) {
        return null;
      }

      // Lease royalty rate: the one the type fixes, else the user's or the default
      const defaultRate = getDefaultRoyaltyRateForInstrumentType(
        instrumentType,
        config
      );
      const leaseRoyaltyRate =
        fixedNumber(allowed, 'leaseRoyaltyRate') ??
        percentageToDecimal(leaseRoyaltyRatePercent) ??
        defaultRate;

      if (leaseRoyaltyRate === null) {
        return null;
      }

      // Build request payload
      const payload: InterestsCalculatorRequest = {
        input_source: 'acreage',
        interests_to_calculate: interestsToCalculate,
        gross_acres: grossAcres!,
        tract_net_acres: tractNetAcres!,
        net_acreage_units: netAcreageUnits,
        lease_royalty_rate: leaseRoyaltyRate,
      };

      // Burdens: only those the interest type carries
      if (!isUnusedField(allowed, 'royaltyBurdens')) {
        const royaltyBurdens = percentageToDecimal(royaltyBurdensPercent);
        if (royaltyBurdens !== null) {
          payload.royalty_burdens = royaltyBurdens;
        }
      }

      if (!isUnusedField(allowed, 'orriBurdens')) {
        const orriBurdens = percentageToDecimal(orriBurdensPercent);
        if (orriBurdens !== null) {
          payload.orri_burdens = orriBurdens;
        }
      }

      // Make API call
      const response = await request.post<
        InterestsCalculatorResponse,
        { error: string }
      >('/interests_calculator', payload, new AbortController());

      if (response.type === 'success') {
        return convertCalculatorResponse(response.data);
      } else if (response.type === 'error') {
        throw new Error(
          response.data?.error ?? 'Failed to calculate interests'
        );
      } else {
        throw new Error(response.message ?? 'Failed to calculate interests');
      }
    }
  );

/**
 * Unified atom that provides the current interest data.
 * Prefers calculated data (from create mode or recalculation),
 * falls back to persisted data from bundleOfSticksDetailsAtom (edit mode via Pusher/workflow).
 */
export const interestDataAtom = atom(
  (get): BundleOfSticksOwnershipResponse | null => {
    const calculatedLoadable = get(calculateInterestsLoadableAtom);
    const persistedLoadable = get(bundleOfSticksDetailsLoadableAtom);

    // Prefer calculated data if available
    if (
      calculatedLoadable.state === 'hasData' &&
      calculatedLoadable.data !== null
    ) {
      return calculatedLoadable.data;
    }

    // Fall back to persisted data from bundleOfSticksDetailsAtom
    // Normalize to handle Rails BigDecimal serialization as strings
    if (
      persistedLoadable.state === 'hasData' &&
      persistedLoadable.data !== null
    ) {
      return normalizeBosResponse(persistedLoadable.data);
    }

    return null;
  }
);

/**
 * Derived atom that converts the interest data to InterestConversionData format.
 */
export const interestConversionDataAtom = atom<InterestConversionData | null>(
  (get) => {
    const data = get(interestDataAtom);
    if (!data) {
      return null;
    }
    return buildInterestConversionData(data);
  }
);

/**
 * Atom that provides unified loading state for interest calculations.
 */
export const interestCalculationLoadingAtom = atom((get): boolean => {
  const calculatedLoadable = get(calculateInterestsLoadableAtom);
  const persistedLoadable = get(bundleOfSticksDetailsLoadableAtom);

  return (
    calculatedLoadable.state === 'loading' ||
    persistedLoadable.state === 'loading'
  );
});
